import { CustomFormFieldFG, FormFieldFG, FormFieldTypeFG, SelectFormFieldFG } from '@hwm/ui-lib';
import { t, Trans } from '@lingui/macro';
import { Typography } from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { DataOptionsFn, IntervalOptionsFn } from '@shared/components/Filters';
import {
  DataOptionsType,
  IntervalOptionsType,
  DigitalIntervalOptionsFn,
  DigitalIntervalOptionsType,
  PreferencesFormModel,
  ChannelUnitsModel,
  GraphExportType,
} from '@shared/models';

export const graphPreferencesFieldsConfiguration = (
  type: string,
  setType: React.Dispatch<React.SetStateAction<string>>,
  unitPreferencesOptions: Record<string, ChannelUnitsModel[]>,
  graphExports: GraphExportType[],
  digitalIntervals: DigitalIntervalOptionsType[],
  cumulativeModes: DigitalIntervalOptionsType[]
): FormFieldFG[] => {
  return [
    {
      label: '',
      property: 'graphTitle',
      type: FormFieldTypeFG.CustomField,
      renderer: ({ dataTestId }) => {
        return (
          <Typography data-testid={`${dataTestId}-customField-graphTitle`}>
            <Trans>Graph</Trans>
          </Typography>
        );
      },
    } as CustomFormFieldFG,
    {
      label: '',
      property: 'graphInterval',
      type: FormFieldTypeFG.Select,
      availableValues: Object.values(IntervalOptionsType).map((value) => ({
        label: IntervalOptionsFn()[value],
        value,
      })),
      returnKeyOnly: true,
      innerSelectLabel: t`Interval`,
      containerClasses: 'mb-4',
    } as SelectFormFieldFG,
    {
      label: '',
      property: 'graphDataType',
      type: FormFieldTypeFG.Select,
      availableValues: Object.values(DataOptionsType).map((value) => ({
        label: DataOptionsFn()[value],
        value,
      })),
      returnKeyOnly: true,
      innerSelectLabel: t`Data`,
      containerClasses: 'mb-4',
    } as SelectFormFieldFG,
    {
      label: '',
      property: 'graphExportFormat',
      type: FormFieldTypeFG.Select,
      availableValues: graphExports.map((value) => ({ label: value, value })),
      returnKeyOnly: true,
      innerSelectLabel: t`Export`,
      containerClasses: 'mb-3',
    } as SelectFormFieldFG,
    {
      label: '',
      property: 'graphDigitalInterval',
      type: FormFieldTypeFG.Select,
      availableValues: digitalIntervals.map((value) => ({
        label: DigitalIntervalOptionsFn()[value],
        value,
      })),
      returnKeyOnly: true,
      innerSelectLabel: t`Digital Interval`,
      containerClasses: 'mb-3',
    } as SelectFormFieldFG,
    {
      label: '',
      property: 'graphCumulativeMode',
      type: FormFieldTypeFG.Select,
      availableValues: cumulativeModes.map((value) => ({
        label: DigitalIntervalOptionsFn()[value],
        value,
      })),
      returnKeyOnly: true,
      innerSelectLabel: t`CumulativeMode`,
      containerClasses: 'mb-3',
    } as SelectFormFieldFG,
    {
      label: t`Alarm Boundaries As Percentage`,
      property: 'graphShowBoundariesAsPercentage',
      type: FormFieldTypeFG.Checkbox,
      containerClasses: 'mb-4',
    },
    {
      label: '',
      property: 'typeUnitTitle',
      type: FormFieldTypeFG.CustomField,
      renderer: () => {
        return (
          <div className="grid grid-cols-2">
            <Typography align="center" variant="caption">
              <Trans>Type</Trans>
            </Typography>
            <Typography align="center" variant="caption">
              <Trans>Unit</Trans>
            </Typography>
          </div>
        );
      },
    } as CustomFormFieldFG,
    {
      label: '',
      property: 'typeUnit',
      type: FormFieldTypeFG.CustomField,
      renderer: ({
        values,
        setFieldValue,
      }: {
        values: PreferencesFormModel;
        setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
      }) => {
        return (
          <div className="flex flex-row overflow-hidden" style={{ height: 212, maxHeight: 212 }}>
            <ToggleButtonGroup
              orientation="vertical"
              value={type}
              exclusive
              style={{
                width: 140,
                overflowY: 'auto',
                overflowX: 'hidden',
                marginRight: 4,
              }}
              onChange={(_, val) => val !== null && setType(val)}
            >
              {Object.keys(values.graphUnitPreferences).map((val) => (
                <ToggleButton key={val} value={val} style={{ outline: 'none', padding: 8, fontSize: 13 }}>
                  {val}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <ToggleButtonGroup
              orientation="vertical"
              value={values.graphUnitPreferences[type] ?? ''}
              exclusive
              style={{
                width: 140,
                overflowY: 'auto',
                overflowX: 'hidden',
                marginRight: 4,
              }}
              onChange={(_, val) => {
                if (val !== null) {
                  const newState = { ...values.graphUnitPreferences };
                  newState[type] = val;
                  setFieldValue('graphUnitPreferences', newState);
                }
              }}
            >
              {unitPreferencesOptions[type]?.map((unit, i) => (
                <ToggleButton
                  key={unit.name + i}
                  value={unit.name}
                  style={{ outline: 'none', padding: 8, fontSize: 13 }}
                >
                  {unit.name}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </div>
        );
      },
      containerClasses: 'mb-4',
    } as CustomFormFieldFG,
  ];
};
